/**
 * ConfirmDialog — wraps DS Dialog for the title + body + two-button pattern.
 *
 * Covers the common "Are you sure?" / "Delete X?" confirmation shape.
 *
 * Usage:
 *   <ConfirmDialog
 *     open={showDelete}
 *     onClose={() => setShowDelete(false)}
 *     onConfirm={handleDelete}
 *     title="Delete user?"
 *     confirmLabel="Delete"
 *     variant="destructive"
 *     blocking
 *   >
 *     This action cannot be undone.
 *   </ConfirmDialog>
 */

import React from 'react'
import styled from 'styled-components'
import {Dialog as DSDialog, DialogContent, DialogActions} from '~/src/design-system'
import {Button} from '~/src/design-system/Primitives'
import {X} from 'lucide-react'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface ConfirmDialogProps {
  /** Controls dialog visibility */
  open: boolean
  /** Called when the dialog requests to close (cancel button, X, ESC, backdrop) */
  onClose: () => void
  /** Called when the confirm button is clicked */
  onConfirm: () => void
  /** Dialog title text */
  title: string
  /** Body content — either children or the `message` shorthand */
  children?: React.ReactNode
  /** Shorthand for a plain-text body (ignored when children are provided) */
  message?: string
  /** Confirm button label. Default: "Confirm" */
  confirmLabel?: string
  /** Cancel button label. Default: "Cancel" */
  cancelLabel?: string
  /** 'default' uses primary confirm button; 'destructive' uses danger-filled */
  variant?: 'default' | 'destructive'
  /**
   * When true, ESC and backdrop click do NOT dismiss the dialog.
   * Use for destructive confirmations that require an explicit choice.
   */
  blocking?: boolean
  /** Disables both buttons and shows spinner on confirm */
  loading?: boolean
  /** Max width of the dialog paper. Default: 480px */
  maxWidth?: string
  /** Optional data-testid on the root dialog element */
  'data-testid'?: string
  /** Override testid for the close button (default: `${testId}-close-button`) */
  closeButtonTestId?: string
  /** Override testid for the cancel button (default: `${testId}-cancel-button`) */
  cancelButtonTestId?: string
  /** Override testid for the confirm button (default: `${testId}-confirm-button`) */
  confirmButtonTestId?: string
  /** Optional aria-labelledby override */
  'aria-labelledby'?: string
  /** Optional aria-describedby */
  'aria-describedby'?: string
}

/* ------------------------------------------------------------------ */
/*  Styled overrides                                                   */
/* ------------------------------------------------------------------ */

const SizedDialog = styled(DSDialog)<{$maxWidth: string}>`
  .MuiDialog-paper {
    max-width: ${({$maxWidth}) => $maxWidth};
  }
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-5) var(--space-6) var(--space-4);
  border-bottom: 1px solid var(--border-default, #e5e7eb);
`

const Title = styled.h2`
  margin: 0;
  font-size: var(--fs-lg, 1.25rem);
  font-weight: var(--fw-semibold, 600);
  line-height: var(--lh-tight, 1.25);
  color: var(--text-primary, #111827);
`

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  children,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'default',
  blocking = false,
  loading = false,
  maxWidth = '480px',
  'data-testid': testId,
  closeButtonTestId,
  cancelButtonTestId,
  confirmButtonTestId,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
}) => {
  const handleDialogClose = (_event: {}, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (blocking) return
    onClose()
  }

  const body = children ?? (message ? <p style={{margin: 0}}>{message}</p> : null)

  return (
    <SizedDialog
      open={open}
      onClose={handleDialogClose}
      $maxWidth={maxWidth}
      aria-labelledby={ariaLabelledBy ?? 'confirm-dialog-title'}
      aria-describedby={ariaDescribedBy}
      data-testid={testId}>
      <Header>
        <Title id="confirm-dialog-title">{title}</Title>
        {!blocking && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            aria-label="Close dialog"
            data-testid={closeButtonTestId ?? (testId ? `${testId}-close-button` : undefined)}
            iconLeft={<X size={18} />}
          />
        )}
      </Header>

      {body && <DialogContent>{body}</DialogContent>}

      <DialogActions>
        <Button
          variant="secondary"
          onClick={onClose}
          disabled={loading}
          data-testid={cancelButtonTestId ?? (testId ? `${testId}-cancel-button` : undefined)}>
          {cancelLabel}
        </Button>
        <Button
          variant={variant === 'destructive' ? 'danger-filled' : 'primary'}
          onClick={onConfirm}
          loading={loading}
          disabled={loading}
          data-testid={confirmButtonTestId ?? (testId ? `${testId}-confirm-button` : undefined)}>
          {confirmLabel}
        </Button>
      </DialogActions>
    </SizedDialog>
  )
}

export default ConfirmDialog
